import * as React from "react";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

// Phase 12: Modernize form control — matches the refreshed Input.
const textareaVariants = cva(
  "flex min-h-[80px] w-full rounded-xl border px-3.5 py-2.5 text-sm transition-colors " +
    "placeholder:text-slate-400 " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 " +
    "disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "border-slate-200 bg-slate-50 text-slate-900 hover:border-slate-300 focus-visible:border-brand-400 focus-visible:bg-white " +
          "dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:border-slate-600 dark:focus-visible:bg-slate-800",
        error:
          "border-rose-300 bg-rose-50 text-rose-900 placeholder:text-rose-400 focus-visible:ring-rose-500/30 focus-visible:border-rose-500 " +
          "dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-200",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    VariantProps<typeof textareaVariants> {
  autoResize?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, variant, autoResize = false, onChange, ...props }, ref) => {
    const internalRef = React.useRef<HTMLTextAreaElement | null>(null);

    const handleResize = React.useCallback(() => {
      const textarea = internalRef.current;
      if (textarea && autoResize) {
        textarea.style.height = "auto";
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    }, [autoResize]);

    React.useEffect(() => {
      handleResize();
    }, [handleResize, props.value]);

    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange?.(e);
        if (autoResize) {
          handleResize();
        }
      },
      [onChange, autoResize, handleResize]
    );

    return (
      <textarea
        className={cn(
          textareaVariants({ variant, className }),
          autoResize && "resize-none overflow-hidden"
        )}
        ref={(node) => {
          internalRef.current = node;
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        onChange={handleChange}
        {...props}
      />
    );
  }
);
Textarea.displayName = "Textarea";

export { Textarea, textareaVariants };
